<script setup lang="ts">
import type { GraphqlDatatableColumn } from '~/components/GqlDatatable.vue';
import gql from 'graphql-tag';
import { computed, ref } from 'vue';
import { CompromissoForm } from '~/lib/fw';
import { useAuthenticationStore } from '~/stores/authenticationStore';

interface Props {
  pastaCodigos?: string[];
  compromisso?: any;
  fluxo?: any;
}

const props = defineProps<Props>();
const emit = defineEmits(['close']);

const tiposOcorrenciaAtendimentoGql = gql`
  query tiposOcorrenciaAtendimentoQuery {
    data: tiposOcorrenciaAtendimento {
      value: codigo
      categoria
      tabulacao
      quando
      label
    }
  }
`;

const mostrarHistoricoSolicitacoes = ref('h');
const colunas: GraphqlDatatableColumn[] = [
  {
    key: 'compromisso',
    title: 'COMPROMISSO',
    width: 120,
  },
  {
    key: 'responsavel',
    title: 'RESPONSÁVEL',
    width: 120,
  },
  {
    dataIndex: 'dataInicioText',
    key: 'dataInicio',
    sortBy: ['dataInicio'],
    sorter: true,
    title: 'SOLICITADO EM',
    width: 120,
    defaultSortOrder: 'descend',
  },
  {
    dataIndex: 'dataVencimentoText',
    key: 'dataVencimento',
    sortBy: ['dataVencimento'],
    sorter: true,
    title: 'VENCIMENTO EM',
    width: 120,
  },
  {
    dataIndex: 'dataConclusaoText',
    key: 'dataConclusao',
    sortBy: ['dataConclusao'],
    sorter: true,
    title: 'CONCLUÍDO EM',
    width: 120,
  },
];

const authenticationStore = useAuthenticationStore();
const buttonsDisabled = computed(() => {
  if (!props.compromisso) {
    return false;
  }
  return authenticationStore.user!.id !== props.compromisso.responsavelCodigo;
});

const tipoOcorrenciaAtendimentoQuando = ref<string | null>(null);

function onTipoOcorrenciaAtendimentoChange(record: any) {
  tipoOcorrenciaAtendimentoQuando.value = record.option.quando;
}
</script>

<template>
  <CompromissoForm
    action="ocorrencia-atendimento/registrar-ocorrencia-atendimento"
    :pasta-codigos="props.pastaCodigos ?? []"
    :compromisso-codigos="props.compromisso ? [props.compromisso.codigo] : []"
    :wait-for-result="true"
    :show-upload-files="true"
    :submit-button-disabled="buttonsDisabled"
    @close="() => emit('close')"
  >
    <template #form="{ model }">
      <a-collapse
        v-if="props.pastaCodigos?.length === 1"
        v-model:active-key="mostrarHistoricoSolicitacoes"
        style="margin-bottom: 24px"
      >
        <a-collapse-panel key="h" header="HISTÓRICO DE SOLICITAÇÕES">
          <CompromissoGqlDatatable
            :columns="colunas"
            :show-dates-filter="false"
            :show-reload-button="false"
            :show-status-search-bar="false"
            :show-filter="false"
            :variables="{
              order: [{ dataInicio: 'DESC' }],
              where: {
                tipo: {
                  eq: 119,
                },
                fluxo: {
                  pastaCodigo: {
                    in: props.pastaCodigos,
                  },
                },
              },
            }"
          />
        </a-collapse-panel>
      </a-collapse>

      <a-form-item
        label="Ocorrência de atendimento"
        name="tipoOcorrenciaAtendimento"
        :rules="[
          {
            message: 'Ocorrência de atendimento é obrigatório',
            required: true,
            type: 'object',
          },
        ]"
      >
        <GqlSelect
          v-model:value="model.tipoOcorrenciaAtendimento"
          :query="tiposOcorrenciaAtendimentoGql"
          placeholder="Selecione a ocorrência de atendimento"
          @change="onTipoOcorrenciaAtendimentoChange"
        />
      </a-form-item>

      <a-alert
        v-if="tipoOcorrenciaAtendimentoQuando !== null"
        :message="tipoOcorrenciaAtendimentoQuando"
        type="warning"
        show-icon
        style="margin-bottom: 24px"
      />

      <a-form-item
        label="Observação"
        name="observacao"
        style="padding-bottom: 0"
      >
        <a-textarea v-model:value="model.observacao" :rows="4" />
      </a-form-item>
    </template>
  </CompromissoForm>
</template>
